import './assets/main.css'

import { createPinia } from 'pinia'
import { createApp } from 'vue'

/* internationalization*/
import i18n, { syncDocumentLang } from './i18n'

/* Sentry */
import * as Sentry from '@sentry/vue'

/* primevue*/
import AccordionContent from 'primevue/accordioncontent'
import AccordionHeader from 'primevue/accordionheader'
import AccordionPanel from 'primevue/accordionpanel'
import AutoComplete from 'primevue/autocomplete'
import Avatar from 'primevue/avatar'
import Badge from 'primevue/badge'
import Breadcrumb from 'primevue/breadcrumb'
import Button from 'primevue/button'
import SplitButton from 'primevue/splitbutton'
import Card from 'primevue/card'
import Checkbox from 'primevue/checkbox'
import Chip from 'primevue/chip'
import Column from 'primevue/column'
import PrimeVue from 'primevue/config'
import ConfirmDialog from 'primevue/confirmdialog'
import DataTable from 'primevue/datatable'
import DatePicker from 'primevue/datepicker'
import Dialog from 'primevue/dialog'
import Divider from 'primevue/divider'
import Fieldset from 'primevue/fieldset'
import FileUpload from 'primevue/fileupload'
import FloatLabel from 'primevue/floatlabel'
import Image from 'primevue/image'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Listbox from 'primevue/listbox'
import Menu from 'primevue/menu'
import Menubar from 'primevue/menubar'
import Message from 'primevue/message'
import MultiSelect from 'primevue/multiselect'
import PanelMenu from 'primevue/panelmenu'
import Password from 'primevue/password'
import Popover from 'primevue/popover'
import ProgressSpinner from 'primevue/progressspinner'
import RadioButton from 'primevue/radiobutton'
import Select from 'primevue/select'
import SelectButton from 'primevue/selectbutton'
import Skeleton from 'primevue/skeleton'
import Step from 'primevue/step'
import StepList from 'primevue/steplist'
import Stepper from 'primevue/stepper'
import Tab from 'primevue/tab'
import TabList from 'primevue/tablist'
import Tabs from 'primevue/tabs'
import Tag from 'primevue/tag'
import Textarea from 'primevue/textarea'
import Toast from 'primevue/toast'
import ToggleSwitch from 'primevue/toggleswitch'
import Toolbar from 'primevue/toolbar'
import TreeTable from 'primevue/treetable'

import { FilterService } from '@primevue/core/api'
import Aura from '@primevue/themes/aura'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'

import { definePreset } from '@primevue/themes'
import moment from 'moment'
import 'primeflex/primeflex.css'
import 'primeicons/primeicons.css'
import App from './App.vue'
import type { Bill } from './model/Bill'
import type Customer from './model/Customer'
import { type Payload } from './model/DeliveryNote'
import type Employee from './model/Employee'
import type Vehicle from './model/Vehicle'
import router from './router'
import { authService } from './services/AuthService'
import { useProfileStore } from './store/profile.module'
import { clearStaleClientReloadGuard, registerStaleClientRecovery } from './utils/staleClientRecovery'

const app = createApp(App)

const pinia = createPinia()
app.use(pinia)

// i18n
app.use(i18n)
syncDocumentLang(i18n.global.locale.value)

// Custom filters
FilterService.register('employeeFilter', (value: Employee, filter): boolean => {
  if (filter === undefined || filter === null || filter.trim() === '') {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return (
    value.name.toString().toLowerCase().includes(filter.toString().toLowerCase()) ||
    value.surname.toString().toLowerCase().includes(filter.toString().toLowerCase())
  )
})

FilterService.register('vehiclesFilter', (value: Vehicle[], filter): boolean => {
  if (filter === undefined || filter === null || filter.trim() === '') {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return (
    value.filter((v) => {
      return v.name.toString().toLowerCase().includes(filter.toString().toLowerCase())
    }).length > 0
  )
})

FilterService.register('vehiclesFilterMultiple', (value: Vehicle[], ids: string[]): boolean => {
  if (ids === undefined || ids === null || ids.length == 0) {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return (
    value.filter((v) => {
      return ids.includes(v.id)
    }).length > 0
  )
})

FilterService.register('employeeFilterMultiple', (value: Employee, ids: string[]): boolean => {
  if (ids === undefined || ids === null || ids.length == 0) {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return ids.includes(value.id)
})

FilterService.register('vehicleFilterMultiple', (value: Vehicle, ids: string[]): boolean => {
  if (ids === undefined || ids === null || ids.length == 0) {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return ids.includes(value.id)
})

FilterService.register('customerFilterMultiple', (value: Customer, ids: string[]): boolean => {
  if (ids === undefined || ids === null || ids.length == 0) {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return ids.includes(value.id)
})

FilterService.register('dateFilter', (value, filter): boolean => {
  if (filter === undefined || filter === null || filter.length === 0) {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  // if (filter.length === 1) {
  //   return moment(value).isSameOrAfter(moment(filter[0]))
  // }

  if (filter.length === 2 && filter[1] !== undefined && filter[1] !== null) {
    return (
      moment(value).isSameOrAfter(moment(filter[0]).startOf('day')) &&
      moment(value).isSameOrBefore(moment(filter[1]).endOf('day'))
    )
  }

  return true
})

FilterService.register('payloadsFilter', (value: Payload[], filter): boolean => {
  if (filter === undefined || filter === null || filter.trim() === '') {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return (
    value.filter((v) => {
      return v.payload.toString().toLowerCase().includes(filter.toString().toLowerCase())
    }).length > 0
  )
})

FilterService.register(
  'billedFilter',
  (value: Bill | undefined, filter: boolean | undefined): boolean => {
    if (filter === undefined || filter === null) {
      return true
    }

    if (filter === true) {
      return value?.id !== undefined
    } else {
      return value?.id === undefined
    }
  }
)


const MiPreset = definePreset(Aura, {
  common: {
    font: {
      family: {
        base: `'Inter var', sans-serif`
      }
    }
  },
  // otros tokens si quieres ajustar colores, espaciados, etc.
});

const primeVueLocale = i18n.global.locale.value === 'es' ? 'es' : 'en'
const primeVueMessages = await import(`./locales/${primeVueLocale}.json`)

app.use(PrimeVue, {
  ripple: true,
  locale: primeVueMessages.primevue,
  theme: {
    preset: primeVueLocale === 'es' ? MiPreset : Aura,
    options: {
      darkModeSelector: '.dark-mode-disabled'
    }
  }
})

// Primevue components
app.component('AccordionPanel', AccordionPanel)
app.component('AccordionHeader', AccordionHeader)
app.component('AccordionContent', AccordionContent)
app.component('AutoComplete', AutoComplete)
app.component('Avatar', Avatar)
app.component('Badge', Badge)
app.component('Breadcrumb', Breadcrumb)
app.component('Button', Button)
app.component('SplitButton', SplitButton)
app.component('DatePicker', DatePicker)
app.component('Card', Card)
app.component('Checkbox', Checkbox)
app.component('Chip', Chip)
app.component('Column', Column)
app.component('ConfirmDialog', ConfirmDialog)
app.component('DataTable', DataTable)
app.component('Dialog', Dialog)
app.component('Divider', Divider)
app.component('Select', Select)
app.component('Dropdown', Select) // Backwards compatibility alias
app.component('SelectButton', SelectButton)
app.component('TreeTable', TreeTable)
app.component('Fieldset', Fieldset)
app.component('FileUpload', FileUpload)
app.component('FloatLabel', FloatLabel)
app.component('InputNumber', InputNumber)
app.component('ToggleSwitch', ToggleSwitch)
app.component('InputText', InputText)
app.component('Listbox', Listbox)
app.component('Menu', Menu)
app.component('Menubar', Menubar)
app.component('Message', Message)
app.component('MultiSelect', MultiSelect)
app.component('Popover', Popover)
app.component('OverlayPanel', Popover) // Backwards compatibility alias
app.component('PanelMenu', PanelMenu)
app.component('Password', Password)
app.component('ProgressSpinner', ProgressSpinner)
app.component('RadioButton', RadioButton)
app.component('Skeleton', Skeleton)
app.component('Stepper', Stepper)
app.component('StepList', StepList)
app.component('Step', Step)
app.component('Tabs', Tabs)
app.component('TabList', TabList)
app.component('Tab', Tab)
app.component('Tag', Tag)
app.component('Textarea', Textarea)
app.component('Toast', Toast)
app.component('Toolbar', Toolbar)
app.component('Image', Image)

app.use(ToastService)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)

const isDevMode = import.meta.env.DEV

Sentry.init({
  app,
  dsn: 'https://01ec735ffbc9e9c605e88b4c564cccdf@o4507351225204736.ingest.de.sentry.io/4507351264657488',
  enableLogs: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    Sentry.replayIntegration({
    // NOTE: This will disable built-in masking. Only use this if your site has no sensitive data, or if you've already set up other options for masking or blocking relevant data, such as 'ignore', 'block', 'mask' and 'maskFn'.
    maskAllText: false,
    blockAllMedia: false,
    networkDetailAllowUrls: [
      window.location.origin,
      "api.kargotr.com",
      /^https:\/\/api\.kargotr\.com/,
    ],
  }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/api\.kargotr\.com/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  enabled: !isDevMode // Disable on dev mode
})

registerStaleClientRecovery(router)

async function bootstrap() {
  await authService.initAuth()
  if (await authService.isAuthenticated()) {
    await useProfileStore().ensureProfileLoaded()
  }

  app.use(router)
  app.mount('#app')
  clearStaleClientReloadGuard()
}

bootstrap()
